// /**
//  * Breadcrumb Utility Functions
//  * 
//  * These utilities help map URL paths to breadcrumb trails and vice versa.
//  * Used for navigation synchronization and clickable breadcrumbs.
//  */

// /**
//  * Builds a comprehensive mapping from URL paths to breadcrumb information
//  * 
//  * @param {Array} menuData - Raw menu data from API (client_schema_name[2])
//  * @returns {Object} pathMapping - Map of URL path to breadcrumb info
//  * 
//  * Example output:
//  * {
//  *   "/employee_registration": {
//  *     breadcrumb: "Admin > Employee > New Employee",
//  *     levels: [
//  *       { name: "Admin", path: null },
//  *       { name: "Employee", path: null },
//  *       { name: "New Employee", path: "/employee_registration" }
//  *     ]
//  *   }
//  * }
//  */
// export const buildPathToBreadcrumbMapping = (menuData) => {
//   if (!menuData || !Array.isArray(menuData)) return {};

//   const pathMapping = {};
  
//   // First pass: collect all paths for each level combination
//   // This helps find if a parent level has its own navigable path
//   const levelPaths = {};
  
//   menuData.forEach((item) => {
//     const { LVL1, LVL2, LVL3, ui_path } = item;
    
//     if (ui_path) {
//       // Store path for LVL1 > LVL2 combination (when LVL3 is empty)
//       if (LVL1 && LVL2 && !LVL3) {
//         const key = `${LVL1}|${LVL2}`;
//         levelPaths[key] = ui_path;
//       }
      
//       // Store path for LVL1 only (if exists)
//       if (LVL1 && !LVL2 && !LVL3) {
//         levelPaths[LVL1] = ui_path;
//       }
//     }
//   });

//   // Second pass: build complete breadcrumb mapping
//   menuData.forEach((item) => {
//     const { LVL1, LVL2, LVL3, ui_path, ui_element, screen_id } = item;
    
//     if (!ui_path) return;
    
//     const levels = [];
//     const breadcrumbParts = [];
    
//     // Level 1
//     if (LVL1) {
//       breadcrumbParts.push(LVL1);
//       levels.push({
//         name: LVL1,
//         path: levelPaths[LVL1] || null, // Path if LVL1 is directly navigable
//         level: 1
//       });
//     }
    
//     // Level 2
//     if (LVL2) {
//       breadcrumbParts.push(LVL2);
//       const lvl2Key = `${LVL1}|${LVL2}`;
//       levels.push({
//         name: LVL2,
//         path: levelPaths[lvl2Key] || null, // Path if LVL2 is directly navigable
//         level: 2
//       });
//     }
    
//     // Level 3
//     if (LVL3) {
//       breadcrumbParts.push(LVL3);
//       levels.push({
//         name: LVL3,
//         path: ui_path,
//         level: 3
//       });
//     } else if (LVL2) {
//       // If no LVL3, the LVL2 item itself is the destination
//       // Update the last level's path
//       levels[levels.length - 1].path = ui_path;
//     } else if (LVL1) {
//       // If only LVL1, it's the destination
//       levels[levels.length - 1].path = ui_path;
//     }
    
//     pathMapping[ui_path] = {
//       breadcrumb: breadcrumbParts.join(" > "),
//       levels: levels,
//       ui_element: ui_element,
//       screen_id: screen_id
//     };
//   });

//   return pathMapping;
// };

// /**
//  * Builds a reverse mapping from breadcrumb text to URL path
//  * Useful for finding navigation path from breadcrumb segment
//  * 
//  * @param {Array} menuData - Raw menu data from API
//  * @returns {Object} Map of "LVL1 > LVL2 > LVL3" to URL path
//  */
// export const buildBreadcrumbToPathMapping = (menuData) => {
//   if (!menuData || !Array.isArray(menuData)) return {};

//   const breadcrumbToPath = {};
  
//   menuData.forEach((item) => {
//     const { LVL1, LVL2, LVL3, ui_path } = item;
    
//     if (!ui_path) return;
    
//     const parts = [LVL1, LVL2, LVL3].filter(Boolean);
//     const breadcrumb = parts.join(" > ");
    
//     breadcrumbToPath[breadcrumb] = ui_path;
    
//     // Also store partial paths for parent navigation
//     // e.g., "Admin > Employee" should map to its path if it has one
//     if (LVL1 && LVL2 && !LVL3) {
//       breadcrumbToPath[`${LVL1} > ${LVL2}`] = ui_path;
//     }
//     if (LVL1 && !LVL2 && !LVL3) {
//       breadcrumbToPath[LVL1] = ui_path;
//     }
//   });

//   return breadcrumbToPath;
// };

// /**
//  * Gets breadcrumb info for a given URL path
//  * 
//  * @param {string} urlPath - Current URL path (e.g., "/employee_registration")
//  * @param {Object} pathMapping - The mapping object from buildPathToBreadcrumbMapping
//  * @returns {Object|null} Breadcrumb info or null if not found
//  */
// export const getBreadcrumbForPath = (urlPath, pathMapping) => {
//   if (!urlPath || !pathMapping) return null;
  
//   // Handle root path
//   if (urlPath === "/" || urlPath === "") {
//     return {
//       breadcrumb: "Home",
//       levels: [{ name: "Home", path: "/", level: 0 }]
//     };
//   }
  
//   return pathMapping[urlPath] || null;
// };

// /**
//  * Parses a breadcrumb string into clickable segments with paths
//  * 
//  * @param {string} breadcrumbString - e.g., "Admin > Employee > New Employee"
//  * @param {Object} pathMapping - The path mapping object
//  * @param {Array} menuData - Raw menu data for finding parent paths
//  * @returns {Array} Array of { name, path, isClickable }
//  */
// export const parseBreadcrumbToSegments = (breadcrumbString, pathMapping, menuData) => {
//   if (!breadcrumbString || breadcrumbString === "Home") {
//     return [{ name: "Home", path: "/", isClickable: true, isLast: true }];
//   }

//   const segments = breadcrumbString.split(" > ").map(s => s.trim());
//   const result = [];
  
//   // Build breadcrumb-to-path lookup
//   const breadcrumbToPath = buildBreadcrumbToPathMapping(menuData || []);
  
//   segments.forEach((segment, index) => {
//     const isLast = index === segments.length - 1;
    
//     // Build partial breadcrumb up to this segment
//     const partialBreadcrumb = segments.slice(0, index + 1).join(" > ");
    
//     // Find if this partial path has a navigable route
//     const path = breadcrumbToPath[partialBreadcrumb] || null;
    
//     result.push({
//       name: segment,
//       path: path,
//       isClickable: !!path && !isLast, // Clickable if has path and not the current page
//       isLast: isLast
//     });
//   });

//   return result;
// };

// /**
//  * Stores breadcrumb state in sessionStorage (survives refresh, cleared on tab close)
//  * Using sessionStorage instead of localStorage to handle refresh better
//  */
// export const storeBreadcrumbState = (urlPath, breadcrumbInfo) => {
//   try {
//     const state = {
//       urlPath,
//       breadcrumb: breadcrumbInfo?.breadcrumb || "Home",
//       timestamp: Date.now()
//     };
//     sessionStorage.setItem("breadcrumbState", JSON.stringify(state));
//   } catch (e) {
//     console.warn("Failed to store breadcrumb state:", e);
//   }
// };

// /**
//  * Retrieves stored breadcrumb state
//  */
// export const getStoredBreadcrumbState = () => {
//   try {
//     const stored = sessionStorage.getItem("breadcrumbState");
//     return stored ? JSON.parse(stored) : null;
//   } catch (e) {
//     console.warn("Failed to retrieve breadcrumb state:", e);
//     return null;
//   }
// };

// export default {
//   buildPathToBreadcrumbMapping,
//   buildBreadcrumbToPathMapping,
//   getBreadcrumbForPath,
//   parseBreadcrumbToSegments,
//   storeBreadcrumbState,
//   getStoredBreadcrumbState
// };












/**
 * Breadcrumb Utility Functions
 *
 * Rule: the ui_path/ui_element belongs to the MOST RECENT child that has a value.
 *   - LVL3 has value  → ui_path is for LVL3. LVL1 and LVL2 are folders (no path of their own).
 *   - LVL3 is empty   → ui_path is for LVL2. LVL1 is a folder (no path of its own).
 *   - LVL2 is empty   → ui_path is for LVL1.
 *
 * A parent segment is only shown as a clickable link if it ITSELF appears as a
 * ui_path somewhere in the menu data. It never inherits a child's path.
 */

const hasValue = (v) => v !== null && v !== undefined && v !== "";

/**
 * Builds: ui_path → { breadcrumb, levels }
 * Used to resolve current URL → breadcrumb string on navigation/refresh.
 */
export const buildPathToBreadcrumbMapping = (menuData) => {
  if (!menuData || !Array.isArray(menuData)) return {};

  // Collect which paths belong directly to which level labels.
  // A label only gets a path if it IS the leaf for some menu entry.
  const lvl1DirectPaths = {};  // LVL1 label → path   (only when LVL1 is the leaf)
  const lvl2DirectPaths = {};  // "LVL1|LVL2" → path  (only when LVL2 is the leaf)

  menuData.forEach(({ LVL1, LVL2, LVL3, ui_path }) => {
    if (!ui_path) return;
    const lvl1 = hasValue(LVL1) ? LVL1 : null;
    const lvl2 = hasValue(LVL2) ? LVL2 : null;
    const lvl3 = hasValue(LVL3) ? LVL3 : null;

    if (lvl1 && !lvl2 && !lvl3) {
      // LVL1 is the leaf
      if (!lvl1DirectPaths[lvl1]) lvl1DirectPaths[lvl1] = ui_path;
    }
    if (lvl1 && lvl2 && !lvl3) {
      // LVL2 is the leaf — record it, but do NOT assign to LVL1
      const key = `${lvl1}|${lvl2}`;
      if (!lvl2DirectPaths[key]) lvl2DirectPaths[key] = ui_path;
    }
    // LVL3 is the leaf → neither LVL1 nor LVL2 get a path from this entry
  });

  const pathMapping = {};

  menuData.forEach(({ LVL1, LVL2, LVL3, ui_path, ui_element, screen_id }) => {
    if (!ui_path) return;
    const lvl1 = hasValue(LVL1) ? LVL1 : null;
    const lvl2 = hasValue(LVL2) ? LVL2 : null;
    const lvl3 = hasValue(LVL3) ? LVL3 : null;

    const breadcrumbParts = [];
    const levels = [];

    if (lvl1) {
      breadcrumbParts.push(lvl1);
      // LVL1 only gets a path if it is itself a direct leaf somewhere in the menu
      levels.push({
        name: lvl1,
        path: lvl1DirectPaths[lvl1] || null,
        level: 1,
      });
    }

    if (lvl2) {
      breadcrumbParts.push(lvl2);
      const key = `${lvl1}|${lvl2}`;
      // LVL2 only gets a path if it is itself a direct leaf (LVL3 empty) somewhere
      levels.push({
        name: lvl2,
        path: lvl2DirectPaths[key] || null,
        level: 2,
      });
    }

    if (lvl3) {
      // LVL3 is the leaf — this entry's ui_path belongs here
      breadcrumbParts.push(lvl3);
      levels.push({ name: lvl3, path: ui_path, level: 3 });
    } else if (lvl2) {
      // LVL2 is the leaf — update its path to this entry's ui_path
      levels[levels.length - 1].path = ui_path;
    } else if (lvl1) {
      // LVL1 is the leaf
      levels[levels.length - 1].path = ui_path;
    }

    pathMapping[ui_path] = {
      breadcrumb: breadcrumbParts.join(" > "),
      levels,
      ui_element,
      screen_id,
    };
  });

  return pathMapping;
};

/**
 * Builds: breadcrumb string (full or partial) → ui_path
 * Used by parseBreadcrumbToSegments to find paths for each segment.
 * Parent segments only get a path if they ARE a leaf in some menu entry.
 */
export const buildBreadcrumbToPathMapping = (menuData) => {
  if (!menuData || !Array.isArray(menuData)) return {};

  const breadcrumbToPath = {};

  menuData.forEach(({ LVL1, LVL2, LVL3, ui_path }) => {
    if (!ui_path) return;
    const lvl1 = hasValue(LVL1) ? LVL1 : null;
    const lvl2 = hasValue(LVL2) ? LVL2 : null;
    const lvl3 = hasValue(LVL3) ? LVL3 : null;

    const parts = [lvl1, lvl2, lvl3].filter(Boolean);
    const fullKey = parts.join(" > ");

    // Always store the full leaf key (first occurrence wins for dupes)
    if (!breadcrumbToPath[fullKey]) breadcrumbToPath[fullKey] = ui_path;

    // Store partial keys ONLY if this entry IS the leaf at that level
    if (lvl1 && !lvl2 && !lvl3) {
      // LVL1 is the leaf
      if (!breadcrumbToPath[lvl1]) breadcrumbToPath[lvl1] = ui_path;
    }

    if (lvl1 && lvl2 && !lvl3) {
      // LVL2 is the leaf — store "LVL1 > LVL2" partial key
      // but do NOT store LVL1 alone (it's still a folder here)
      const partial = `${lvl1} > ${lvl2}`;
      if (!breadcrumbToPath[partial]) breadcrumbToPath[partial] = ui_path;
    }

    // LVL3 is the leaf → no partial keys for LVL1 or LVL2 from this entry
  });

  return breadcrumbToPath;
};

export const getBreadcrumbForPath = (urlPath, pathMapping) => {
  if (!urlPath || !pathMapping) return null;
  if (urlPath === "/" || urlPath === "") {
    return { breadcrumb: "Home", levels: [{ name: "Home", path: "/", level: 0 }] };
  }
  return pathMapping[urlPath] || null;
};

export const parseBreadcrumbToSegments = (breadcrumbString, pathMapping, menuData) => {
  if (!breadcrumbString || breadcrumbString === "Home") {
    return [{ name: "Home", path: "/", isClickable: true, isLast: true }];
  }

  const segments = breadcrumbString.split(" > ").map((s) => s.trim());
  const breadcrumbToPath = buildBreadcrumbToPathMapping(menuData || []);
  const result = [];

  segments.forEach((segment, index) => {
    const isLast = index === segments.length - 1;
    const partialBreadcrumb = segments.slice(0, index + 1).join(" > ");
    const path = breadcrumbToPath[partialBreadcrumb] || null;

    result.push({
      name: segment,
      path,
      // Clickable only if it has its own direct path AND is not the current page
      isClickable: !!path,  // leaf is also clickable — it's the back-link when navigating deeper
      isLast,
    });
  });

  return result;
};

export const storeBreadcrumbState = (urlPath, breadcrumbInfo) => {
  try {
    sessionStorage.setItem("breadcrumbState", JSON.stringify({
      urlPath,
      breadcrumb: breadcrumbInfo?.breadcrumb || "Home",
      timestamp: Date.now(),
    }));
  } catch (e) {
    console.warn("Failed to store breadcrumb state:", e);
  }
};

export const getStoredBreadcrumbState = () => {
  try {
    const stored = sessionStorage.getItem("breadcrumbState");
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    console.warn("Failed to retrieve breadcrumb state:", e);
    return null;
  }
};

export default {
  buildPathToBreadcrumbMapping,
  buildBreadcrumbToPathMapping,
  getBreadcrumbForPath,
  parseBreadcrumbToSegments,
  storeBreadcrumbState,
  getStoredBreadcrumbState,
};